import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  LockClosedIcon,
} from 'components/icons';

import api from '../services/api';
import { useAuth } from '../hooks/useAuth';

// =============================================================================
// OAuth consent
// =============================================================================
//
// The only human step in the sign-in flow, and the only place a person decides
// what an agent may read. Everything else in OAuth is machinery; this screen is
// the actual security control, so it is built to be read rather than clicked
// past:
//
//   - The client's name and the exact redirect URI are shown verbatim. Someone
//     who knows what they are looking at can spot a redirect they did not
//     expect, which is the one attack a consent screen can catch.
//   - Scopes are listed individually and can be unticked. A consent screen with
//     no choice is a notification wearing a button.
//   - Deny is a real button, the same size as Approve, and it sends the user
//     back to the client with an error rather than leaving them stranded.
//
// The request is validated by the server before this renders, so a request that
// is going to fail fails here — not after someone has approved it.

interface ClientInfo {
  name: string;
  uri: string | null;
  redirect_uri: string;
  first_seen: string;
}

// The only permission that costs the user money, so the only one not pre-ticked
// below. Every other grant here is undone by disconnecting the app; a spent
// credit is not.
const SPEND_SCOPE = 'run:analysis';

interface ScopeInfo {
  id: string;
  description: string;
}

const OAuthAuthorize: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const [client, setClient] = useState<ClientInfo | null>(null);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [granted, setGranted] = useState<string[]>([]);
  const [readOnly, setReadOnly] = useState(true);
  const [spendsCredits, setSpendsCredits] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const state = params.get('state') ?? '';
  const codeChallenge = params.get('code_challenge') ?? '';
  const codeChallengeMethod = params.get('code_challenge_method') ?? '';
  const responseType = params.get('response_type') ?? 'code';
  const scopeParam = params.get('scope') ?? '';

  // Signing in must return the visitor to this exact request, query string and
  // all. Dropping them on the dashboard would strip the PKCE challenge and make
  // the client start over with no explanation.
  useEffect(() => {
    if (authLoading || user) return;
    const returnTo = `${window.location.pathname}${window.location.search}`;
    navigate(`/login?redirect=${encodeURIComponent(returnTo)}`, { replace: true });
  }, [authLoading, user, navigate]);

  const loadInfo = useCallback(async () => {
    try {
      const res = await api.get('/oauth/authorize-info', {
        params: {
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType,
          scope: scopeParam || undefined,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod || undefined,
        },
      });
      setClient(res.data.client);
      setScopes(res.data.scopes);
      // Everything is pre-ticked except the permission to spend money. Consent
      // screens are approved in one click by almost everyone, and a default
      // that costs the user something should not ride along on that click.
      setGranted(
        res.data.scopes.map((s: ScopeInfo) => s.id).filter((id: string) => id !== SPEND_SCOPE),
      );
      setReadOnly(res.data.read_only);
      setSpendsCredits(!!res.data.spends_credits);
    } catch (err: any) {
      setError(
        err?.response?.data?.error_description ||
          'This sign-in request is not valid. Ask the application to try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, redirectUri, responseType, scopeParam, codeChallenge, codeChallengeMethod]);

  useEffect(() => {
    if (user) loadInfo();
  }, [user, loadInfo]);

  const approve = async () => {
    if (submitting || granted.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post('/oauth/authorize', {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: responseType,
        scope: scopeParam || undefined,
        granted_scopes: granted,
        state: state || undefined,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod || undefined,
      });
      // A full navigation, not a router push: the destination belongs to the
      // client application and is usually not on this origin at all.
      window.location.href = res.data.redirect_to;
    } catch (err: any) {
      setError(err?.response?.data?.error_description || 'Could not complete the sign-in.');
      setSubmitting(false);
    }
  };

  const deny = () => {
    // Sent back to the client with the error the spec defines, so it can say
    // "you declined" instead of hanging on a callback that never arrives.
    if (!client) {
      navigate('/dashboard');
      return;
    }
    try {
      const url = new URL(client.redirect_uri);
      url.searchParams.set('error', 'access_denied');
      url.searchParams.set('error_description', 'The user declined the request.');
      if (state) url.searchParams.set('state', state);
      window.location.href = url.toString();
    } catch {
      navigate('/dashboard');
    }
  };

  if (authLoading || (!user && !error)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-sunk">
        <p className="text-content-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunk px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <span className="text-2xl font-bold gradient-text">Clevor</span>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-8 shadow-lg">
          {loading ? (
            <p className="text-center text-content-muted">Checking the request…</p>
          ) : error ? (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h1 className="text-center text-title text-content">This request can't be completed</h1>
              <p className="mt-3 text-center text-body text-content-muted">{error}</p>
              <Link
                to="/dashboard"
                className="mt-6 block w-full rounded-lg bg-surface-sunk py-2.5 text-center text-sm font-medium text-content hover:bg-gray-200"
              >
                Back to Clevor
              </Link>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50">
                <ShieldCheckIcon className="h-6 w-6 text-brand-600" />
              </div>

              <h1 className="text-center text-title-lg text-balance text-content">
                Connect <span className="gradient-text">{client?.name}</span> to Clevor?
              </h1>
              <p className="mt-3 text-center text-body text-content-muted">
                Signed in as {user?.email}. It will act for you, using only what you allow below.
              </p>

              {readOnly && (
                <div className="mt-6 flex items-start gap-2.5 rounded-lg bg-emerald-50 p-3 ring-1 ring-emerald-200">
                  <LockClosedIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <p className="text-sm text-emerald-900">
                    <span className="font-semibold">Read-only.</span> It cannot change anything in
                    your account and cannot spend your AI credits.
                  </p>
                </div>
              )}

              <fieldset className="mt-6">
                <legend className="mb-2 text-sm font-semibold text-content">
                  What it will be able to do
                </legend>
                <div className="space-y-2">
                  {scopes.filter((sc) => sc.id !== SPEND_SCOPE).map((scope) => (
                    <label
                      key={scope.id}
                      className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:border-border-strong"
                    >
                      <input
                        type="checkbox"
                        checked={granted.includes(scope.id)}
                        onChange={(e) =>
                          setGranted((prev) =>
                            e.target.checked
                              ? [...prev, scope.id]
                              : prev.filter((s) => s !== scope.id),
                          )
                        }
                        className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm">
                        <span className="block text-content">{scope.description}</span>
                        <code className="text-xs text-content-subtle">{scope.id}</code>
                      </span>
                    </label>
                  ))}
                </div>
                {spendsCredits && (
                  <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-amber-300 bg-amber-50/60 p-3">
                    <input
                      type="checkbox"
                      checked={granted.includes(SPEND_SCOPE)}
                      onChange={(e) =>
                        setGranted((prev) =>
                          e.target.checked
                            ? [...prev, SPEND_SCOPE]
                            : prev.filter((s) => s !== SPEND_SCOPE),
                        )
                      }
                      className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm">
                      <span className="block font-medium text-content">
                        Let it spend your AI credits
                      </span>
                      <span className="mt-0.5 block text-content-muted">
                        For AI analysis, partner matching and market research — the same work the
                        buttons in Clevor do, at the same cost. Left off, it can still read
                        everything above. You can disconnect it at any time, but credits it spends
                        are spent.
                      </span>
                      <code className="mt-1 block text-xs text-content-subtle">{SPEND_SCOPE}</code>
                    </span>
                  </label>
                )}
                {granted.length === 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    Allow at least one, or choose Deny.
                  </p>
                )}
              </fieldset>

              {/* Shown because it is the one thing on this page that can catch
                  an attack: a code sent somewhere the user did not expect. */}
              <div className="mt-5 rounded-lg bg-surface-sunk p-3">
                <p className="text-xs text-content-subtle">Codes will be sent to</p>
                <code className="mt-0.5 block break-all text-xs text-content-muted">
                  {client?.redirect_uri}
                </code>
                <p className="mt-2 text-xs text-content-subtle">
                  If you don't recognise this, choose Deny.
                </p>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={deny}
                  disabled={submitting}
                  className="flex-1 rounded-lg border border-border py-2.5 text-sm font-semibold text-content-muted transition-colors hover:border-border-strong hover:text-content disabled:opacity-60"
                >
                  Deny
                </button>
                <button
                  onClick={approve}
                  disabled={submitting || granted.length === 0}
                  className="btn-primary flex flex-1 items-center justify-center gap-1.5 py-2.5 disabled:opacity-60"
                >
                  {submitting ? (
                    'Connecting…'
                  ) : (
                    <>
                      <CheckIcon className="h-4 w-4" /> Allow
                    </>
                  )}
                </button>
              </div>

              <p className="mt-4 text-center text-xs text-content-subtle">
                You can disconnect this at any time in{' '}
                <Link to="/settings?tab=api-keys" className="underline hover:text-content-muted">
                  Settings → Agent Access
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthAuthorize;
